import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Controller,
    FormProvider,
    useForm,
    useFormContext,
    useFormState,
    useWatch,
} from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { AsyncPaginate } from "react-select-async-paginate";
import FormInputText from "../shared/FormInputText";
import FormSelect from "../shared/FormSelect";
import FormCheckbox from "../shared/FormCheckbox";
import {
    FormHeader,
    FormArrayEditor,
    RATE_TYPE_OPTIONS,
    RATE_SET_KEY_BY_DEF_KEY,
    buildComponentDefaultValues,
    mappingKeyOf,
} from "./BillingRateBuilderShared";
import { componentSchema, specificRateSchema } from "./billingRateBuilderSchemas";
import {
    COMPONENT_CREATE_REASON,
    COVERAGE_TIER_MAPPINGS,
    RATE_SUBCATEGORY,
    applyComponentCollisionAmendment,
    formatRateAmountString,
    sanitizeHrpAsciiIdentifier,
} from "../../utils/billingRateUtils.js";
import { checkBillingRateComponentCollisions } from "../../utils/apiUtils";
import { customStyles } from "../../utils/styleUtils";

const COVERAGE_TIER_SET_PAGE_SIZE = 10;

const formatEditableAmount = (value) => {
    if (value == null || value === "") return value;
    return formatRateAmountString(value, { allowZero: true }) ?? String(value);
};

const buildPayloadFromValues = (values, originalPayload) => {
    const isNew = !originalPayload;
    const rateTypeKey = values.rateType?.value;
    const rateSetKey = RATE_SET_KEY_BY_DEF_KEY[rateTypeKey];
    const setName = values.coverageTierSetName?.value;
    // Component Name and Component ID are edited as separate fields but must stay
    // whitespace-normalized the same way the extraction pipeline normalizes them
    // (sanitizeHrpAsciiIdentifier). Otherwise a stray double space on one field but
    // not the other lets the pre-submit collision check miss an existing HRP
    // component that HRP itself matches on the sanitized componentId, so the
    // submission is sent as Create and HRP rejects it as a duplicate.
    const sanitizedComponentName = sanitizeHrpAsciiIdentifier(values.componentName);
    const sanitizedComponentId = sanitizeHrpAsciiIdentifier(values.componentId);

    const rateSet = {
        ...(values.tiered
            ? {
                specificRates: (values.specificRates ?? []).map((r) => ({
                    members: { coverageTierName: r?.coverageTierName?.value },
                    value: { amount: formatEditableAmount(r?.amount) },
                })),
            }
            : {}),
        defaultRate: { amount: formatEditableAmount(values.defaultRateAmount) },
    };

    const definition = {
        rateClassifications: {
            componentId: sanitizedComponentId,
            rateSubCategory: {
                codeSetName: RATE_SUBCATEGORY.codeSetName,
                shortName: values.rateSubCategory?.label ?? null,
            },
        },
        ...(values.tiered ? { coverageTierSetName: setName } : {}),
    };

    const originalBrcp = originalPayload?.billingRateComponentPayload ?? {};
    const newBrcpDefaults = isNew
        ? {
            maintenanceInfo: { transactionType: "Create", overrideAction: false },
            maintenanceReasonCode: COMPONENT_CREATE_REASON,
            maintenanceComment: values.description,
        }
        : {};

    const hasAmount = values.tiered
        ? (values.specificRates ?? []).some((r) => r?.amount != null && r?.amount !== "")
        : values.defaultRateAmount != null && values.defaultRateAmount !== "";

    // The selected codeEntry is kept on the wrapper's sidecar match metadata so it
    // stays off the payload's rateSubCategory. A manual selection is treated as a
    // confirmed match so it is never flagged as an approximate match needing review.
    const subCategoryMatch = values.rateSubCategory?.value
        ? {
            codeEntry: values.rateSubCategory.value,
            matchType: null,
            matchedShortName: null,
            confirmed: true,
        }
        : null;

    return {
        ...originalPayload,
        tiered: values.tiered,
        renewalRequired: hasAmount ? false : (originalPayload?.renewalRequired ?? false),
        subCategoryMatch,
        billingRateComponentPayload: {
            ...newBrcpDefaults,
            ...originalBrcp,
            billingRateComponent: {
                componentName: sanitizedComponentName,
                description: values.description,
                definition,
                ...(rateTypeKey && rateSetKey
                    ? { [rateTypeKey]: { [rateSetKey]: rateSet } }
                    : {}),
            },
        },
    };
};

const SpecificRateEditor = ({ prefix, coverageTierNameOptions }) => {
    const { control, register } = useFormContext();
    const { errors } = useFormState({ control });

    return (
        <div className="billing-rate-builder__label-grid">
            <FormSelect
                name={`${prefix}.coverageTierName`}
                label="Coverage Tier Name"
                control={control}
                options={coverageTierNameOptions}
                errors={errors}
            />
            <FormInputText
                name={`${prefix}.amount`}
                label="Rate Amount"
                register={register}
                errors={errors}
            />
        </div>
    );
};

const ComponentEditFormBody = ({ rateSubCategoryOptions, coverageTierSetRows, fees }) => {
    const { control, register, getValues, setValue } = useFormContext();
    const { errors } = useFormState({ control });
    const tiered = useWatch({ control, name: "tiered" });
    const prevTieredRef = useRef(tiered);
    useEffect(() => {
        if (tiered && !prevTieredRef.current) {
            setValue("defaultRateAmount", "0.00", { shouldDirty: true, shouldValidate: true });
        }
        prevTieredRef.current = tiered;
    }, [tiered, setValue]);

    const coverageTierSetOption = useWatch({ control, name: "coverageTierSetName" });
    const mappingKey = mappingKeyOf(coverageTierSetOption?.value);
    const coverageTierNameOptions = mappingKey
        ? Object.values(COVERAGE_TIER_MAPPINGS[mappingKey] ?? {})
            .flat()
            .map((n) => ({ value: n, label: n }))
        : [];

    const handleCoverageTierSetChange = useCallback(
        (option) => {
            const nextMappingKey = mappingKeyOf(option?.value);
            const tierSet = nextMappingKey ? COVERAGE_TIER_MAPPINGS[nextMappingKey] : null;
            if (!tierSet) return;

            const componentName = String(getValues("componentName") ?? "")
                .trim()
                .replace(/^\S+\s*/, "");
            const fee =
                (fees ?? []).find((f) => f?.description && f.description === componentName);

            const tierNames = fee?.lookup2 != null ? tierSet[fee.lookup2] : null;
            if (!Array.isArray(tierNames)) return;

            const currentRates = getValues("specificRates") ?? [];
            setValue(
                "specificRates",
                tierNames.map((name) => ({
                    coverageTierName: { value: name, label: name },
                    amount: currentRates[0]?.amount ?? "",
                })),
                { shouldDirty: true, shouldValidate: true },
            );
        },
        [fees, getValues, setValue],
    );

    const loadCoverageTierSetOptions = useCallback(
        async (search, loadedOptions) => {
            const allNames = (coverageTierSetRows ?? [])
                .map((r) => r?.coverageTierSetName)
                .filter((n) => n != null);
            const filtered = search
                ? allNames.filter((n) =>
                    String(n).toLowerCase().includes(search.toLowerCase()),
                )
                : allNames;
            const offset = loadedOptions.length;
            const page = filtered.slice(offset, offset + COVERAGE_TIER_SET_PAGE_SIZE);
            return {
                options: page.map((n) => ({ value: n, label: n })),
                hasMore: offset + COVERAGE_TIER_SET_PAGE_SIZE < filtered.length,
            };
        },
        [coverageTierSetRows],
    );

    return (
        <div className="billing-rate-builder__modal-content-body">
            <div className="billing-rate-builder__label-grid">
                <FormInputText
                    name="componentName"
                    label="Component Name"
                    register={register}
                    errors={errors}
                    maxLength={50}
                    control={control}
                />
                <FormInputText
                    name="description"
                    label="Description"
                    register={register}
                    errors={errors}
                    maxLength={100}
                    control={control}
                />
                <FormInputText
                    name="componentId"
                    label="Component ID"
                    register={register}
                    errors={errors}
                    maxLength={50}
                    control={control}
                />
                <FormSelect
                    name="rateSubCategory"
                    label="Rate Sub Category"
                    control={control}
                    options={rateSubCategoryOptions}
                    errors={errors}
                />
                <FormSelect
                    name="rateType"
                    label="Rate Type"
                    control={control}
                    options={RATE_TYPE_OPTIONS}
                    errors={errors}
                />
                <FormInputText
                    name="defaultRateAmount"
                    label="Default Rate Amount"
                    register={register}
                    errors={errors}
                />
                <FormCheckbox name="tiered" label="Tiered" control={control} />
                {tiered && (
                    <div className="form-input">
                        <label>Coverage Tier Set Name</label>
                        <Controller
                            name="coverageTierSetName"
                            control={control}
                            render={({ field }) => (
                                <AsyncPaginate
                                    {...field}
                                    onChange={(option) => {
                                        field.onChange(option);
                                        handleCoverageTierSetChange(option);
                                    }}
                                    placeholder="select..."
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    debounceTimeout={300}
                                    loadOptions={loadCoverageTierSetOptions}
                                    cacheUniqs={[coverageTierSetRows]}
                                    styles={customStyles}
                                    isClearable
                                />
                            )}
                        />
                        {errors?.coverageTierSetName?.message && (
                            <p style={{ color: "var(--red)", paddingTop: ".4rem" }}>
                                {errors.coverageTierSetName.message}
                            </p>
                        )}
                    </div>
                )}
            </div>

            {tiered && (
                <FormArrayEditor
                    name="specificRates"
                    label="Specific Rates"
                    headers={["Coverage Tier Name", "Rate Amount"]}
                    error={errors?.specificRates?.message ?? errors?.specificRates?.root?.message}
                    validateRow={(row) => specificRateSchema.safeParse(row).success}
                    getRow={(r) => [r?.coverageTierName?.label ?? "", r?.amount ?? ""]}
                    makeNew={() => ({
                        coverageTierName: null,
                        amount: "",
                    })}
                    renderEditor={(prefix) => (
                        <SpecificRateEditor
                            prefix={prefix}
                            coverageTierNameOptions={coverageTierNameOptions}
                        />
                    )}
                />
            )}
        </div>
    );
};

const ComponentEditForm = ({
    initial,
    subCategoryRows,
    coverageTierSetRows,
    fees,
    groupId,
    onBack,
    onSave,
}) => {
    const rateSubCategoryOptions = useMemo(
        () =>
            (subCategoryRows ?? []).map((r) => ({
                value: r?.cdEntryTxt,
                label: r?.shortNm,
            })),
        [subCategoryRows],
    );

    const resolver = useCallback(
        async (values, context, options) => {
            const result = await zodResolver(componentSchema)(values, context, options);
            if (result.errors?.rateSubCategory) return result;
            const selected = values.rateSubCategory?.value;
            const inList = rateSubCategoryOptions.some((o) => o.value === selected);
            if (selected != null && rateSubCategoryOptions.length > 0 && !inList) {
                result.errors = {
                    ...result.errors,
                    rateSubCategory: {
                        type: "invalid",
                        message: "Value must be selected from the list",
                    },
                };
            }
            return result;
        },
        [rateSubCategoryOptions],
    );

    const defaultValues = useMemo(() => {
        const base = buildComponentDefaultValues(initial);
        const storedCodeEntry = base.rateSubCategory?.value;
        const matchedOption = storedCodeEntry
            ? (rateSubCategoryOptions.find((o) => o.value === storedCodeEntry) ?? null)
            : null;
        return { ...base, rateSubCategory: matchedOption };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const methods = useForm({
        mode: "all",
        resolver,
        defaultValues,
    });

    const isAdding = !initial;
    const { isValid } = methods.formState;
    const [isSaving, setIsSaving] = useState(false);

    // Trigger full validation immediately when opening an existing payload for
    // editing so errors are visible before the user touches any field.
    useEffect(() => {
        if (!isAdding) {
            methods.trigger();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSave = methods.handleSubmit(async (values) => {
        const payload = buildPayloadFromValues(values, initial);
        const savedComponent = payload.billingRateComponentPayload.billingRateComponent;

        // If the componentName changed, re-check collisions for just this
        // component against HRP and apply the amendment to this payload so its
        // maintenance info reflects an amend when the new name already exists.
        // Compare sanitized values (payload) so a whitespace-only edit doesn't
        // skip the re-check.
        const initialName =
            initial?.billingRateComponentPayload?.billingRateComponent?.componentName;
        const nameChanged = (savedComponent.componentName ?? "") !== (initialName ?? "");
        let collisionRows = null;
        if (nameChanged) {
            setIsSaving(true);
            try {
                const collisions = await checkBillingRateComponentCollisions([
                    {
                        componentName: savedComponent.componentName ?? "",
                        componentId: savedComponent.definition?.rateClassifications?.componentId ?? "",
                    },
                ]).catch(() => null);
                collisionRows = Array.isArray(collisions?.rows) ? collisions.rows : [];
                applyComponentCollisionAmendment(payload, collisionRows, groupId);
            } finally {
                setIsSaving(false);
            }
        }

        onSave(payload, collisionRows);
    });

    return (
        <FormProvider {...methods}>
            <FormHeader
                onBack={isAdding ? null : onBack}
                onSave={handleSave}
                saveLabel={isSaving ? "Saving..." : isAdding ? "Create" : "Save"}
                noBackTitle={isAdding ? "Create New Component" : null}
                disabled={!isValid || isSaving}
            />
            <ComponentEditFormBody
                rateSubCategoryOptions={rateSubCategoryOptions}
                coverageTierSetRows={coverageTierSetRows}
                fees={fees}
            />
        </FormProvider>
    );
};

export default ComponentEditForm;
